import React from 'react';

import { IconProp } from '@fortawesome/fontawesome-svg-core';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { zodResolver } from '@hookform/resolvers/zod';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import { useForm } from 'react-hook-form';
import { FormattedMessage } from 'react-intl';
import { useParams } from 'react-router-dom';

import { useGetWorkingHoursException } from 'src/features/workingHoursExceptions/api/useGetWorkingHoursException';
import WorkingHoursExceptionsForm from 'src/features/workingHoursExceptions/components/form/WorkingHoursExceptionsForm';
import {
    workingHoursExceptionsFormSchema,
    WorkingHoursExceptionsFormSchemaValues,
} from 'src/features/workingHoursExceptions/utils/formSchema';
import { WorkingHoursException } from 'src/features/workingHoursExceptions/utils/types';
import SquareIcon from 'src/registry/components/blocks/SquareIcon';
import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { SmartParkError } from 'src/registry/types/axios';
import { Button } from 'src/registry/ui/Button';
import {
    Dialog,
    DialogBody,
    DialogClose,
    DialogContent,
    DialogDescription,
    DialogFooter,
    DialogHeader,
    DialogSeparator,
    DialogTitle,
    DialogTrigger,
} from 'src/registry/ui/Dialog';
import { generalMessages } from 'src/registry/utils/messages';
import { useOnError } from 'src/utils/errors';
import { useParking } from 'src/providers/ParkingProvider';
import { ICON_MAP } from 'src/utils/constants';

interface WorkingHoursFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedWorkingHoursException?: WorkingHoursException;
    children?: React.ReactNode;
    isOpen?: boolean;
    isPending: boolean;
    icon: IconProp;
    title: string;
    subtitle: string;
    mutate: (
        data: WorkingHoursExceptionsFormSchemaValues,
        onSuccess: () => void,
        onError: (error: AxiosError<SmartParkError, any>) => void,
    ) => void;
}

function WorkingHoursExceptionsFormModal<TData>({
    selectedWorkingHoursException,
    className,
    isPending,
    children,
    icon,
    title,
    subtitle,
    mutate,
}: WorkingHoursFormModalProps<TData>) {
    const [isModalOpen, setIsModalOpen] = React.useState(false);

    // We are in edit mode if we have a selected working hours exception
    // otherwise it means we are creating a new working hours exception
    const { siteId = '' } = useParams();
    const { parkingId } = useParking();
    const onError = useOnError();

    const { data: workingHoursException } = useGetWorkingHoursException({
        hoursId: selectedWorkingHoursException?.id ?? '',
        siteId,
        parkingId,
        // We only want to fetch if we're in edit
        config: { enabled: !!selectedWorkingHoursException?.id && isModalOpen },
    });

    const defaultValues = React.useMemo(
        () =>
            workingHoursException
                ? { ...workingHoursException, zone: workingHoursException.zone.id }
                : {
                      all_day: true,
                      start_time: null,
                      end_time: null,
                      zone: '',
                      description: '',
                      is_open: false,
                  },
        [workingHoursException],
    );

    const form = useForm<WorkingHoursExceptionsFormSchemaValues>({
        resolver: zodResolver(workingHoursExceptionsFormSchema),
        defaultValues,
    });

    React.useEffect(() => {
        form.reset(defaultValues);
    }, [workingHoursException, isModalOpen, selectedWorkingHoursException, form, defaultValues]);
    const onOpenChange = (isOpen: boolean) => {
        if (!isOpen) form.reset();

        setIsModalOpen(isOpen);
    };

    const onSubmit = React.useCallback(
        (values: WorkingHoursExceptionsFormSchemaValues) => {
            const newValues = {
                ...values,
                date: dayjs(values.date).format('YYYY-MM-DD'),
            };

            mutate(
                newValues,
                () => setIsModalOpen(false),
                (error) => onError(error, form.setError),
            );
        },
        [mutate, onError, form.setError],
    );
    return (
        <Dialog modal open={isModalOpen} onOpenChange={onOpenChange}>
            {children ? (
                <DialogTrigger asChild>{children}</DialogTrigger>
            ) : (
                <DialogTrigger asChild>
                    <Button className={className}>
                        <FontAwesomeIcon icon={ICON_MAP.ADD} />
                        {title}
                    </Button>
                </DialogTrigger>
            )}
            <DialogContent>
                <DialogHeader>
                    <div className="flex flex-row gap-2">
                        <SquareIcon icon={icon} />
                        <div>
                            <DialogTitle>{title}</DialogTitle>
                            <DialogDescription>{subtitle}</DialogDescription>
                        </div>
                    </div>
                </DialogHeader>
                <DialogSeparator />
                <DialogBody>
                    <WorkingHoursExceptionsForm form={form} onSubmit={onSubmit} />
                </DialogBody>
                <DialogSeparator />
                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" type="button" className="flex-1">
                            <FormattedMessage
                                id={generalMessages.cancel.id}
                                defaultMessage={generalMessages.cancel.defaultMessage}
                            />
                        </Button>
                    </DialogClose>
                    <Button
                        variant="default"
                        disabled={isPending || (selectedWorkingHoursException && !form.formState.isDirty)}
                        type="submit"
                        onClick={form.handleSubmit(onSubmit)}
                        className="flex-1"
                    >
                        {isPending && <FontAwesomeIcon icon={ICON_MAP.SPINNER} className="mr-2 size-4 animate-spin" />}
                        {title}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>
    );
}

export default WorkingHoursExceptionsFormModal;
